import React, { useMemo, useState } from 'react';
import { Maximize2, Minimize2 } from 'lucide-react';

// Ticket 195: an HTML email shown without a sanitiser. The markup goes into a
// sandboxed iframe: no scripts, no same-origin (so it cannot reach the app's
// cookies or storage), and a CSP that blocks every fetch, so remote images
// (tracking pixels) never load. Links open in a new tab, unsandboxed.
export const EMAIL_FRAME_SANDBOX = 'allow-popups allow-popups-to-escape-sandbox';
const EMAIL_FRAME_HEAD = '<base target="_blank"><meta http-equiv="Content-Security-Policy" content="default-src \'none\'; style-src \'unsafe-inline\'; img-src data:">';

// The CSP does not stop the frame navigating itself, which would load a remote
// page (and tell the sender the email was opened). Several things can do that
// without a script: a meta refresh, a link that targets the frame, a link the
// rewrite below cannot see (a <template shadowrootmode> only becomes a shadow
// root once the frame parses it; SVG and MathML links use xlink:href and a SMIL
// <set> can rewrite a target), a nested frame with its own document, or a
// <link rel=preconnect> that the CSP does not cover. So every element outside
// the HTML namespace and every element in STRIP is dropped, the email's own
// <base> and <meta http-equiv> go, and every link opens a new tab.
const HTML_NS = 'http://www.w3.org/1999/xhtml';
const STRIP = 'template, iframe, frame, frameset, object, embed, link, base, meta[http-equiv]';

export function emailFrameDoc(html) {
  const doc = new DOMParser().parseFromString(String(html ?? ''), 'text/html');
  Array.from(doc.querySelectorAll('*'))
    .filter((el) => el.namespaceURI !== HTML_NS)
    .forEach((el) => el.remove());
  doc.querySelectorAll(STRIP).forEach((el) => el.remove());
  doc.querySelectorAll('a, area').forEach((el) => {
    el.setAttribute('target', '_blank');
    el.setAttribute('rel', 'noopener noreferrer');
    el.removeAttribute('ping');
  });
  doc.head.insertAdjacentHTML('afterbegin', EMAIL_FRAME_HEAD);
  return `<!doctype html>${doc.documentElement.outerHTML}`;
}

// A sandboxed frame without allow-same-origin cannot be measured from here,
// so its height is fixed and the frame scrolls itself.
const COLLAPSED_PX = 288;
const EXPANDED_VH = 75;

export default function EmailHtmlFrame({ html, messageId }) {
  const [expanded, setExpanded] = useState(false);
  const srcDoc = useMemo(() => emailFrameDoc(html), [html]);
  const Icon = expanded ? Minimize2 : Maximize2;
  return (
    <div className="w-[32rem] max-w-full" data-testid={`email-frame-wrap-${messageId}`}>
      <iframe
        title="Email content"
        srcDoc={srcDoc}
        sandbox={EMAIL_FRAME_SANDBOX}
        referrerPolicy="no-referrer"
        loading="lazy"
        className="block w-full rounded-lg bg-white border border-slate-200"
        style={{ height: expanded ? `${EXPANDED_VH}vh` : `${COLLAPSED_PX}px` }}
        data-testid={`email-frame-${messageId}`}
      />
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="mt-1 inline-flex items-center gap-1 text-[11px] opacity-80 hover:opacity-100"
        data-testid={`email-frame-toggle-${messageId}`}
      >
        <Icon size={11} /> {expanded ? 'Collapse' : 'Expand'}
      </button>
    </div>
  );
}
